/**
 * ft-company-quarter
 * Quarter selector wrapper using ft-dropdown.
 *
 * Precondition: cik attribute set
 * Postcondition: Renders dropdown with available quarters, fires quarter-change on selection
 *
 * Attributes:
 * - cik: SEC CIK identifier
 * - value: Currently selected quarter (YYYY-QN format)
 *
 * Events:
 * - quarter-change: { quarter: string, reportDate: string }
 */

import { fetchSubmissions } from '../../lib/financeapis.js';
import { parseEdgarSubmissions, parseCompanyData, getQuarter } from '../../lib/parsers.js';

interface QuarterOption {
  quarter: string;
  reportDate: string;
  label: string;
}

class CompanyQuarter extends HTMLElement {
  private quarters: QuarterOption[] = [];
  private selectedQuarter = '';
  private companyName = '';
  private abortController = new AbortController();

  static get observedAttributes(): string[] {
    return ['cik', 'value'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  get cik(): string {
    return this.getAttribute('cik') || '';
  }

  get value(): string {
    return this.selectedQuarter;
  }

  get navigate(): string | null {
    return this.getAttribute('data-navigate');
  }

  connectedCallback(): void {
    this.render();
    if (this.cik) {
      this.loadQuarters();
    }
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || !this.shadowRoot?.children.length) return;

    if (name === 'cik' && newValue) {
      this.loadQuarters();
    } else if (name === 'value' && newValue) {
      this.selectedQuarter = newValue;
      this.updateDropdown();
    }
  }

  private async loadQuarters(): Promise<void> {
    const result = await fetchSubmissions(this.cik);
    if (!result.ok) return;

    const submissions = parseEdgarSubmissions(result.data);
    const company = parseCompanyData(result.data);

    // Get company name
    this.companyName = company.name || '';

    // Build unique quarters list (most recent first)
    const seen = new Set<string>();
    this.quarters = [];

    for (const sub of submissions) {
      if (!sub.reportDate) continue;
      const quarter = getQuarter(sub.reportDate);
      if (seen.has(quarter)) continue;
      seen.add(quarter);

      const [year, q] = quarter.split('-');
      this.quarters.push({
        quarter,
        reportDate: sub.reportDate,
        label: `${q} ${year}`,
      });
    }

    // Sort by quarter descending (YYYY-QN format sorts correctly)
    this.quarters.sort((a, b) => b.quarter.localeCompare(a.quarter));

    // Default to latest quarter
    if (this.quarters.length > 0 && !this.selectedQuarter) {
      this.selectedQuarter = this.quarters[0].quarter;
    }

    this.render();
    this.updateDropdown();
    this.setupEventListeners();
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>${this.getStyles()}</style>
      <div class="wrapper">
        <ft-dropdown size="small"></ft-dropdown>
        ${this.companyName ? `<span class="sep">·</span><a class="name" href="/companies/${this.cik}">${this.escapeHtml(this.companyName)}</a>` : ''}
      </div>
    `;
  }

  private getStyles(): string {
    return `
      :host {
        display: inline-flex;
        align-items: center;
        font-family: Inter, system-ui, sans-serif;
        font-size: 13px;
      }

      .wrapper {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      ft-dropdown {
        --dropdown-min-width: 100px;
      }

      .name {
        color: var(--muted);
        text-decoration: none;
        max-width: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .name:hover {
        text-decoration: underline;
      }

      .name:focus-visible {
        outline: 2px solid var(--primary, #e0524d);
        outline-offset: 2px;
        border-radius: 2px;
      }

      .sep {
        color: var(--muted);
        opacity: 0.5;
      }
    `;
  }

  private updateDropdown(): void {
    const dropdown = this.shadowRoot?.querySelector('ft-dropdown');
    if (!dropdown) return;

    const options = this.quarters.map(q => ({
      value: q.quarter,
      label: q.label,
    }));

    dropdown.setAttribute('options', JSON.stringify(options));
    if (this.selectedQuarter) {
      dropdown.setAttribute('value', this.selectedQuarter);
    }
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const dropdown = this.shadowRoot?.querySelector('ft-dropdown');

    dropdown?.addEventListener('change', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      this.selectQuarter(detail.value);
    }, { signal });
  }

  private selectQuarter(quarter: string): void {
    if (quarter === this.selectedQuarter) return;

    this.selectedQuarter = quarter;

    if (this.navigate) {
      history.pushState({}, '', this.navigate + quarter);
    }

    const option = this.quarters.find(q => q.quarter === quarter);
    this.dispatchEvent(new CustomEvent('quarter-change', {
      bubbles: true,
      composed: true,
      detail: {
        quarter,
        reportDate: option?.reportDate || '',
      },
    }));
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Public API

  getQuarters(): QuarterOption[] {
    return [...this.quarters];
  }

  setQuarter(quarter: string): void {
    if (this.quarters.some(q => q.quarter === quarter)) {
      this.selectQuarter(quarter);
      this.updateDropdown();
    }
  }
}

if (!customElements.get('ft-company-quarter')) {
  customElements.define('ft-company-quarter', CompanyQuarter);
}
